import { Navigate, Route } from "react-router-dom";
import MyShelfScreen from "./MyShelfScreen";
import LibraryScreen from "../library/LibraryScreen";
import PurchasesScreen from "../purchases/PurchasesScreen";
import CreatorsShelfScreen from "./creators/CreatorsShelfScreen";
import { MonthlyComicsScreen } from "../monthlyComics/MonthlyComicsScreen";
import ReadingListsShelfScreen from "./ReadingListsShelfScreen";
import ReadingListPlaceholder from "./ReadingListPlaceholder";
import ReadingListDetailScreen from "./ReadingListDetailScreen";
import { useAccountData } from "../../hooks/useAccountData";

// Hides every reading-list route until the `reading-lists` feature flag is on.
// We render nothing while account data is loading to avoid a flash, then bounce
// to home if the flag is off.
function ReadingListsFlagGate({ children }: { children: React.ReactNode }) {
  const accountData = useAccountData();
  if (accountData === undefined) return null;
  if (!accountData?.features?.reading_lists) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// Reading-list detail has two entry points:
//
//   - `/reading-lists/:id`             — top-level, no shelf chrome. The
//     canonical *public* URL anyone can share; it stands alone since the
//     viewer may not be the list owner (or even logged in).
//
//   - `/my-shelf/reading-lists/:id`    — nested under MyShelfScreen, so the
//     tabs (Library / Purchases / Lists / etc.) stay visible. This is the
//     URL the rail navigates to when you click a list from your shelf.
//
// Both render ReadingListDetailScreen — they're just two outlets onto the
// same screen. /reading-lists/new and /reading-lists/:id/edit still render
// the placeholder until those flows ship.
export const routes = (
  <>
    <Route path="my-shelf" Component={MyShelfScreen}>
      <Route path="library" Component={LibraryScreen} />
      <Route path="purchases" Component={PurchasesScreen} />
      <Route path="creators" Component={CreatorsShelfScreen} />
      <Route
        path="reading-lists"
        element={
          <ReadingListsFlagGate>
            <ReadingListsShelfScreen />
          </ReadingListsFlagGate>
        }
      />
      <Route
        path="reading-lists/:listId"
        element={
          <ReadingListsFlagGate>
            <ReadingListDetailScreen />
          </ReadingListsFlagGate>
        }
      />
      <Route path="gold-rewards" Component={MonthlyComicsScreen} />
    </Route>
    <Route
      path="reading-lists/new"
      element={
        <ReadingListsFlagGate>
          <ReadingListPlaceholder />
        </ReadingListsFlagGate>
      }
    />
    <Route
      path="reading-lists/:listId/edit"
      element={
        <ReadingListsFlagGate>
          <ReadingListPlaceholder />
        </ReadingListsFlagGate>
      }
    />
    <Route
      path="reading-lists/:listId"
      element={
        <ReadingListsFlagGate>
          <ReadingListDetailScreen />
        </ReadingListsFlagGate>
      }
    />
  </>
);
